import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';

const JobSearch = () => {
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    location: '',
    type: 'all',
    experience: 'all',
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedJobs, setSavedJobs] = useState([]);

  // Load jobs on component mount
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async (searchFilters = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        ...searchFilters,
        limit: 20
      };

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });

      const response = await ApiService.getJobs(params);
      
      if (response.success) {
        const list = response.data?.jobs || response.jobs || [];
        const isBoostActive = (j) => !!j?.boosted && (!j?.boostExpiresAt || new Date(j.boostExpiresAt) > new Date());
        const sorted = [...list].sort((a, b) => {
          const aBoost = isBoostActive(a);
          const bBoost = isBoostActive(b);
          if (aBoost !== bBoost) return aBoost ? -1 : 1;
          if (!!a?.featured !== !!b?.featured) return a?.featured ? -1 : 1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setJobs(sorted);
      } else {
        setError('Failed to load jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const { name, value } = event.target;
    setSearchParams({
      ...searchParams,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const searchFilters = {};
    
    if (searchParams.keyword) {
      searchFilters.search = searchParams.keyword;
    }
    if (searchParams.location) {
      searchFilters.location = searchParams.location;
    }
    if (searchParams.type !== 'all') {
      searchFilters.jobType = searchParams.type;
    }
    if (searchParams.experience !== 'all') {
      searchFilters.experienceLevel = searchParams.experience;
    }

    fetchJobs(searchFilters);
  };

  const toggleSaveJob = (jobId) => {
    // Update local state
    setJobs(jobs.map(job =>
      job._id === jobId ? { ...job, isSaved: !job.isSaved } : job
    ));
    
    // Update saved jobs list
    if (savedJobs.includes(jobId)) {
      setSavedJobs(savedJobs.filter(id => id !== jobId));
    } else {
      setSavedJobs([...savedJobs, jobId]);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Find Your Next Job
      </Typography>

      {/* Search Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                name="keyword"
                label="Job Title or Keywords"
                value={searchParams.keyword}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                name="location"
                label="Location"
                value={searchParams.location}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  name="type"
                  value={searchParams.type}
                  label="Job Type"
                  onChange={handleSearchChange}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="full-time">Full Time</MenuItem>
                  <MenuItem value="part-time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="internship">Internship</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Job Listings */}
      <Grid container spacing={3}>
        {jobs.map((job) => {
          const boostActive = !!job?.boosted && (!job?.boostExpiresAt || new Date(job.boostExpiresAt) > new Date());
          return (
          <Grid item xs={12} key={job._id}>
            <Card sx={{ ...(boostActive ? { backgroundColor: 'rgba(255, 243, 224, 0.6)' } : {}) }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {job.title}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      {boostActive && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                      {!boostActive && job?.featured && (<Chip label="Featured" color="info" size="small" />)}
                    </Box>
                    <Typography color="textSecondary" gutterBottom>
                      {(() => {
                        const normalizeCountry = (c) => (c || '').replace(/\bUSA\b/i, 'Zambia');
                        const city = job.location?.city || '';
                        const country = normalizeCountry(job.location?.country || '');
                        const company = job.companyName || job.company || '';
                        const loc = `${city}${city && country ? ', ' : ''}${country}`.replace(/^,\s*|,\s*$/g, '');
                        return `${company} • ${loc}`.replace(/\s•\s$/, '');
                      })()}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<WorkIcon />}
                        label={job.jobType}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {job.salary && (
                        <Chip
                          label={`$${job.salary.min} - $${job.salary.max}`}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      <Chip
                        label={job.workLocation}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {job.description?.substring(0, 150)}...
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Posted {new Date(job.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title={job.isSaved ? "Remove from saved" : "Save job"}>
                      <IconButton
                        onClick={() => toggleSaveJob(job._id)}
                        color={job.isSaved ? "primary" : "default"}
                      >
                        {job.isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )})}
      </Grid>

      {/* No Jobs Found */}
      {!loading && jobs.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No jobs found matching your criteria
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your search filters
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default JobSearch;